"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { laadFirestore } from "@/lib/firestore-client"; // MOW-PERF-OKT26: Firestore alleen nog voor de terugval
import ProductCard from "@/components/ProductCard";
import ShopSidebar, { ActiveFilterChips, applyFilters, DEFAULT_FILTERS, formatProductCount, ShopFilters } from "@/components/ShopSidebar";
import UspSlider from "@/components/UspSlider";
import FeaturedProductsSlider from "@/components/FeaturedProductsSlider";
import CategoryContentAccordion from "@/components/CategoryContentAccordion";
import CategoryFaqSection from "@/components/CategoryFaqSection";
import type { Product, ColorOption, RichCategory } from "@/types";
import { isProductZichtbaar } from "@/lib/product-visibility";
import { productenVanMerk } from "@/lib/product-list-data"; // MOW-PERF-OKT26
import type { MerkPaginaData } from "@/lib/brand-page-data"; // MOW-PERF-OKT26: alleen het type
import { shouldSpreadCards, spreadCardsPerProduct } from "@/lib/product-sort";
import { t } from "@/lib/site-texts";
import { filtersFromQuery } from "@/lib/filter-query"; // MOW-FILTERLINKS-OKT26
import { useFilterUrlWriter } from "@/lib/use-filter-url-writer"; // MOW-FILTERLINKS-OKT26
// >>> MOW-CATEGORIEFILTER-OKT26
import { useCart } from "@/context/CartContext";
import { productIsOnSale } from "@/lib/product-sale";
import { HOOFD_CATEGORIEEN } from "@/lib/category-filter";
// <<< MOW-CATEGORIEFILTER-OKT26

const PAGE_SIZE = 24;
type CardEntry = { key: string; product: Product; colorOption?: ColorOption };

// MOW-PERF-OKT26: merk, producten en de inhoud van de categorie "Merken" komen van de server
// (lib/brand-page-data.ts, app/merk/[slug]/page.tsx) en staan in de eerste HTML. Alleen als de server
// ze niet kon leveren (Firestore-storing) haalt deze pagina ze zelf op, zoals voorheen.
export default function BrandPage({
  slug,
  initialData,
  initialFilters,
}: {
  slug: string;
  initialData?: MerkPaginaData | null;
  /** De filters uit het adres, door de server al bepaald op de producten van dit merk. */
  initialFilters?: Partial<ShopFilters>;
}) {
  const [brand, setBrand] = useState<MerkPaginaData["brand"] | null>(initialData?.brand ?? null);
  const [products, setProducts] = useState<Product[]>(initialData?.products ?? []);
  const [loading, setLoading] = useState(!initialData);
  const [filters, setFilters] = useState<ShopFilters>({ ...DEFAULT_FILTERS, ...initialFilters });
  // >>> MOW-FILTERLINKS-OKT26: de gekozen filters staan ook in het adres (zie lib/filter-query.ts). Het merk
  // zit al in de route en wordt hier dus niet gelezen of geschreven.
  const filterUrl = useFilterUrlWriter({ skip: ["merk"] });
  const changeFilters = React.useCallback(
    (next: ShopFilters) => {
      setFilters(next);
      filterUrl.write(next);
    },
    [filterUrl]
  );
  // <<< MOW-FILTERLINKS-OKT26
  // Rijke content (SEO-blokken, FAQ, uitgelicht) die de eigenaar invult bij de
  // categorie "Merken" in de admin — hoort op elke individuele merkpagina te
  // verschijnen, niet alleen op het merkenoverzicht (was eerder helemaal niet
  // gekoppeld: deze pagina las nooit van categories/merken).
  const [merkenCategorie, setMerkenCategorie] = useState<RichCategory | null>(initialData?.merkenCategorie ?? null);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>(initialData?.featuredProducts ?? []);

  useEffect(() => {
    if (initialData) return; // de server leverde alles al
    let cancelled = false;
    (async () => {
      try {
        const { db, collection, doc, getDoc, getDocs } = await laadFirestore(); // MOW-PERF-OKT26
        const [brandSnap, prodSnap, merkenCatSnap] = await Promise.all([
          getDoc(doc(db, "brands", slug)),
          getDocs(collection(db, "products")),
          getDoc(doc(db, "categories", "merken")),
        ]);
        if (cancelled) return;
        if (brandSnap.exists()) setBrand({ slug, ...(brandSnap.data() as any) });
        else setBrand({ slug, name: slug.replace(/-/g, " ") });

        const all = prodSnap.docs
          .map((d) => ({ ...d.data(), id: d.id }) as Product)
          .filter(isProductZichtbaar);
        // Matchen op brandSlug of (genormaliseerde) merknaam.
        const mine = productenVanMerk(all, slug);
        setProducts(mine);
        // >>> MOW-FILTERLINKS-OKT26: de filters uit het adres, op het moment dat de producten bekend zijn
        const uitAdres = filtersFromQuery(window.location.search, mine, { skip: ["merk"] });
        if (Object.keys(uitAdres).length > 0) setFilters((f) => ({ ...f, ...uitAdres }));
        // <<< MOW-FILTERLINKS-OKT26

        if (merkenCatSnap.exists()) {
          const cat = { id: "merken", ...merkenCatSnap.data() } as RichCategory;
          setMerkenCategorie(cat);
          const featuredIds = cat.featuredProductIds || [];
          if (featuredIds.length > 0) {
            const byId = new Map(all.map((p) => [p.id, p]));
            setFeaturedProducts(
              featuredIds.map((id) => byId.get(id)).filter((p): p is Product => !!p && isProductZichtbaar(p))
            );
          }
        }
      } catch (e) {
        console.error("Merkpagina laden mislukt:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // MOW-CATEGORIEFILTER-OKT26: Sale als filter, dezelfde regel als /shop?sale=1 (afgeprijsd of onder een lopende actie)
  const { promotions } = useCart();
  const isOnSale = React.useCallback((p: Product) => productIsOnSale(p, promotions), [promotions]);
  const visible = useMemo(() => {
    const lijst = applyFilters(products, filters);
    return filters.sale ? lijst.filter(isOnSale) : lijst;
  }, [products, filters, isOnSale]);

  // Bij "Bestverkocht" en "Nieuwste eerst" staan eerst de eerste kleuren van alle
  // producten, dan de tweede kleuren enz. (lib/product-sort.ts): een product met 33
  // kleuren vulde anders een hele pagina (feedback 14-10-2026).
  const cards = useMemo<CardEntry[]>(() => {
    const kaarten = visible.flatMap((p): CardEntry[] =>
      p.colorOptions && p.colorOptions.length > 0
        ? p.colorOptions.map((opt) => ({ key: `${p.id}-${opt.slug}`, product: p, colorOption: opt }))
        : [{ key: p.id, product: p }]
    );
    return shouldSpreadCards(filters.sort) ? spreadCardsPerProduct(kaarten) : kaarten;
  }, [visible, filters.sort]);

  const [shown, setShown] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { setShown(PAGE_SIZE); }, [filters]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setShown((s) => Math.min(s + PAGE_SIZE, cards.length)); },
      { rootMargin: "600px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [cards.length]);

  const brandName = brand?.name || slug;

  return (
    <div className="container" style={{ paddingTop: "30px", paddingBottom: "60px" }}>
      {/* Breadcrumbs */}
      <div style={{ fontSize: "0.8rem", color: "var(--color-gray-400)", marginBottom: "20px" }}>
        <Link href="/" style={{ color: "inherit", textDecoration: "none" }}>HOME</Link>
        {" / "}
        <Link href="/merken" style={{ color: "inherit", textDecoration: "none" }}>MERKEN</Link>
        {" / "}
        <span style={{ color: "var(--color-secondary)", fontWeight: 500 }}>{brandName.toUpperCase()}</span>
      </div>

      {/* Merk-hero: logo + intro */}
      <div style={{ display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap", marginBottom: "28px" }}>
        {brand?.logoUrl && (
          <div style={{ position: "relative", width: "120px", height: "72px", flexShrink: 0 }}>
            <Image src={brand.logoUrl} alt={brandName} fill sizes="120px" style={{ objectFit: "contain" }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: "260px" }}>
          <h1 style={{ fontSize: "1.9rem", color: "var(--color-secondary)", marginBottom: "8px" }}>{brandName}</h1>
          {brand?.description && (
            <p style={{ color: "var(--color-gray-600)", maxWidth: "70ch", lineHeight: 1.6, textAlign: "justify" }}>{brand.description}</p>
          )}
        </div>
      </div>

      <div className="shop-layout">
        {/* MOW-FILTERS-OKT26 3: resultCount en de tekst tellen kaarten (kleurvarianten), zoals het raster ze toont;
            eerder stond hier het aantal producten ("26 producten van Ten Cate" boven 129 kaarten) */}
        {/* MOW-CATEGORIEFILTER-OKT26: de hele categorieboom (met de aantallen van dit merk) bovenaan het paneel, en Sale */}
        <ShopSidebar
          products={products}
          filters={filters}
          onChange={changeFilters}
          resultCount={cards.length}
          fallbackCategories={HOOFD_CATEGORIEEN}
          listComplete={!loading}
          saleCheck={isOnSale}
        />
        <div>
          {/* MOW-FILTERS-OKT26 4: chips voor alle actieve filters; de merkpagina had er nog geen */}
          <ActiveFilterChips filters={filters} onChange={changeFilters} />
          <p className="results-count" style={{ marginBottom: "20px" }}>
            {loading ? "Laden..." : `${formatProductCount(cards.length)} van ${brandName}`}
          </p>
          {loading ? (
            <div data-t-key="merk.loading" style={{ textAlign: "center", padding: "60px 0", color: "var(--color-gray-400)" }}>{t("merk.loading", "Producten laden...")}</div>
          ) : cards.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-gray-400)" }}>
              <p>{t("merk.empty-prefix", "Geen producten van")} {brandName} {t("merk.empty-suffix", "gevonden.")}</p>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {cards.slice(0, shown).map((c) => (
                  <ProductCard key={c.key} product={c.product} colorOption={c.colorOption} />
                ))}
              </div>
              {shown < cards.length && (
                <div ref={sentinelRef} style={{ textAlign: "center", padding: "30px 0", color: "var(--color-gray-400)", fontSize: "0.85rem" }}>
                  {t("merk.loading-more", "Meer producten laden…")} ({shown} / {cards.length})
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* SEO-blok onder de producten — alleen bij een eigen ingevulde tekst, anders
          herhaalt dit blok woordelijk de intro hierboven (was de bug: viel terug op
          brand.description). */}
      {brand?.seoText && (
        <div style={{ margin: "40px 0 24px", padding: "28px", border: "1px solid #e5e5e5", borderRadius: "8px", background: "#fafafa" }}>
          <h2 style={{ fontSize: "1.2rem", marginBottom: "12px", color: "var(--color-secondary)" }}>Over {brandName}</h2>
          <div style={{ color: "var(--color-gray-600)", lineHeight: 1.7, whiteSpace: "pre-line", maxWidth: "80ch", textAlign: "justify" }}>
            {brand.seoText}
          </div>
        </div>
      )}

      {/* SEO-content, uitgelicht en FAQ van de categorie "Merken" — zelfde blokken
          als op de categoriepagina's, hergebruikt zodat wat de eigenaar bij
          "Merken" in de admin invult ook echt op elke merkpagina verschijnt. */}
      <CategoryContentAccordion title="Merken" contentBlocks={merkenCategorie?.contentBlocks} />
      {featuredProducts.length > 0 && <FeaturedProductsSlider products={featuredProducts} />}
      <CategoryFaqSection title="Merken" faqs={merkenCategorie?.faqs} />

      <UspSlider />
    </div>
  );
}
